import { useId } from "react";
import { FACES, PALETTES, safeId } from "./mascotMood";
import { Face, BackEffects, FrontEffects, TypingDots } from "./faceParts";
import "./mascot.css";

// Notebook-paper note with a dog-eared top-right corner.
export const PAPER_PATH =
  "M34 24 H148 L178 54 V172 Q178 184 166 184 H34 Q22 184 22 172 V36 Q22 24 34 24 Z";
export const VIEWBOX = "-6 -34 212 262";
export const VIEW_W = 212;
export const VIEW_H = 262;

function Accessory({ kind, clipId, pal }) {
  if (kind === "sprout") {
    return (
      <>
        <path d="M100 26 C100 18 98 12 100 6" fill="none" stroke="#065f46" strokeWidth="4" strokeLinecap="round" />
        <path d="M100 12 C92 1 78 3 74 10 C83 17 93 17 100 12Z" fill="#a7f3d0" stroke="#065f46" strokeWidth="3" strokeLinejoin="round" />
        <path d="M100 9 C106 0 118 0 121 6 C114 12 106 13 100 9Z" fill="#a7f3d0" stroke="#065f46" strokeWidth="3" strokeLinejoin="round" />
      </>
    );
  }
  if (kind === "band") {
    return (
      <>
        <g clipPath={`url(#${clipId})`}>
          <rect x="18" y="40" width="164" height="18" fill="#fff" />
          <rect x="18" y="46.5" width="164" height="5" fill="#dc2626" />
        </g>
        <path d="M174 58 L196 50 L193 68 Z M174 62 L194 76 L184 82 Z" fill="#fff" stroke={pal.lip} strokeOpacity=".3" strokeWidth="1.5" strokeLinejoin="round" />
      </>
    );
  }
  if (kind === "crown") {
    return (
      <g transform="translate(104 -4) rotate(8)">
        <path d="M0 28 L3 7 L14 17 L22 1 L30 17 L41 7 L44 28 Z" fill="#fbbf24" stroke="#b45309" strokeWidth="2.5" strokeLinejoin="round" />
        <circle cx="22" cy="21" r="3.2" fill="#fff" />
        <circle cx="10" cy="23" r="2" fill="#fef3c7" />
        <circle cx="34" cy="23" r="2" fill="#fef3c7" />
      </g>
    );
  }
  return null;
}

/**
 * letter  – shows the letter on the paper (picker/onboarding); drops the mouth.
 * mood    – key of FACES; `face` overrides it with a raw face object.
 * variant – "full" centres the face, "peek" lifts it so it clears a card edge.
 */
export default function PaperMascot({
  letter,
  mood = "calm",
  face: faceOverride,
  color = "red",
  accessory,
  size = 120,
  variant = "full",
  shadow = variant === "full",
  label,
  className = "",
}) {
  const uid = safeId(useId());
  const face = faceOverride || FACES[mood] || FACES.calm;
  const pal = PALETTES[color] || PALETTES.red;
  const ch = letter ? String(letter).toUpperCase() : "";
  const ids = { g: `${uid}g`, p: `${uid}p`, c: `${uid}c`, f: `${uid}f` };

  let faceTransform;
  if (ch) faceTransform = "translate(-2 -6)";
  else if (variant === "peek") faceTransform = "translate(-2 0)";
  else faceTransform = "translate(98 102) scale(1.15) translate(-100 -72)";

  const fx = face.fx || [];
  const font = { fontFamily: "'Baloo 2', 'Arial Rounded MT Bold', sans-serif", fontWeight: 800, fontSize: 92, textAnchor: "middle" };

  return (
    <svg
      viewBox={VIEWBOX}
      width={size}
      height={(size * VIEW_H) / VIEW_W}
      className={`nm-mascot ${className}`}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <defs>
        <linearGradient id={ids.g} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={pal.top} />
          <stop offset="1" stopColor={pal.bot} />
        </linearGradient>
        <linearGradient id={ids.p} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#4c1d95" stopOpacity=".5" />
          <stop offset=".55" stopColor="#4c1d95" stopOpacity="0" />
        </linearGradient>
        <clipPath id={ids.c}>
          <path d={PAPER_PATH} />
        </clipPath>
        <filter id={ids.f} x="-10%" y="-10%" width="120%" height="125%">
          <feTurbulence type="fractalNoise" baseFrequency="0.035" numOctaves="2" seed="7" result="warp" />
          <feDisplacementMap in="SourceGraphic" in2="warp" scale="4" xChannelSelector="R" yChannelSelector="G" result="rough" />
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" seed="3" result="noise" />
          <feColorMatrix in="noise" type="saturate" values="0" result="grain" />
          <feComposite in="rough" in2="grain" operator="arithmetic" k1="0.4" k2="0.78" k3="0" k4="0" result="textured" />
          <feComposite in="textured" in2="rough" operator="in" result="clipped" />
          <feDropShadow in="clipped" dx="0" dy="5" stdDeviation="3" floodColor={pal.lip} floodOpacity=".35" />
        </filter>
      </defs>

      {shadow && <ellipse cx="100" cy="214" rx="56" ry="6" fill="rgba(120,20,30,.14)" />}
      <BackEffects face={face} />

      <g className={`nm-a nm-a-${face.a || "none"}`}>
        <g className={`nm-b nm-b-${face.b || "none"}`}>
          <g filter={`url(#${ids.f})`}>
            <path d={PAPER_PATH} fill={`url(#${ids.g})`} />
            <g clipPath={`url(#${ids.c})`} stroke="#fff" strokeOpacity=".16" strokeWidth="2">
              <path d="M22 118 H178 M22 142 H178 M22 166 H178" />
              <path d="M44 24 V184" stroke="#fde68a" strokeOpacity=".45" strokeWidth="1.5" />
            </g>
          </g>
          <path d="M148 24 L178 54 L154 56 Q148 56 148 50 Z" fill={pal.lip} opacity=".35" transform="translate(-2 3)" />
          <path
            className={`nm-flap${face.flap ? ` nm-flap-${face.flap}` : ""}`}
            d="M148 24 L178 54 L154 54 Q148 54 148 48 Z"
            fill={pal.flap}
            stroke={pal.lip}
            strokeOpacity=".25"
            strokeWidth="1.5"
          />
          {fx.includes("pale") && <path d={PAPER_PATH} fill={`url(#${ids.p})`} />}
          {accessory === "band" && <Accessory kind="band" clipId={ids.c} pal={pal} />}
          <Face face={face} transform={faceTransform} showMouth={!ch} />
          {ch && (
            <>
              <text x="98" y="163" {...font} fill={pal.lip} opacity=".55">{ch}</text>
              <text x="98" y="159" {...font} fill="#fff">{ch}</text>
            </>
          )}
          {accessory && accessory !== "band" && <Accessory kind={accessory} pal={pal} />}
          <FrontEffects face={face} tearAt={ch ? [133, 76] : [140, 116]} />
        </g>
      </g>
      {face.dots && <TypingDots />}
    </svg>
  );
}
